import {
  Stack,
  Text,
  Heading,
  Icon,
  Box,
  Button,
  Center,
  VStack,
  useDisclosure,
  Flex,
  Spacer
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';

import { useExplorerPageStore } from '@store/app.store';
import { SFClockIcon } from '@assets/icons';
import { useChatStore } from '@store/chat/chat.store';
import { useCardsCleanBody } from '@services/query-services/cards.query-service';

import CardSearchModal from '@components/search/card-search-modal.component';
import Lottie from 'lottie-react';
import animation from '@assets/animations/lottie/home-page-empty-no-insights.json';
import { useStyledToast } from '@util/hooks';
import ExplorerSourceCard from './explorer-source-card.component';
import { pluralized } from '@util/helpers';

function ExplorerSourcesContent() {
  const activeConversation = useChatStore((state) => state?.conversation);
  const toast = useStyledToast();
  const {
    isOpen: isSearchModalOpen,
    onOpen: onSearchModalOpen,
    onClose: onSearchModalClose
  } = useDisclosure();

  const sourcesFromLocalstorage = useExplorerPageStore((state) => state?.sources);
  const setSourcesInLocalstorage = useExplorerPageStore((state) => state?.setSources);

  const [sources, setSources] = useState([]);

  const isCollectionInitiator = activeConversation?.initiator === 'Collection';
  const { data: cards, isLoading } = useCardsCleanBody(
    isCollectionInitiator
      ? {
          collection_ids: [activeConversation.initiator_data.id],
          asc: false,
          initiative_type: 'Focus',
          page: 1,
          pageSize: 25,
          sort: 'created_at'
        }
      : { ids: sources?.map((source) => source.id), pageSize: 25 },
    {
      enabled: isCollectionInitiator
        ? !!activeConversation?.initiator_data?.id
        : sources?.length > 0
    }
  );

  function removeFn(sourceId) {
    const updatedSources = sources.filter((source) => source.id !== sourceId);
    setSources(updatedSources);
    setSourcesInLocalstorage(updatedSources);
    toast({
      title: 'Source Removed',
      status: 'info',
      duration: 1000,
      isClosable: true
    });
  }

  function addSubmitFn(cardIds) {
    const newSources = cardIds
      .filter((id) => !sources.some((source) => source.id === id))
      .map((id) => ({
        id,
        type: 'Card'
      }));
    const updatedSources = [...sources, ...newSources];
    setSources(updatedSources);
    setSourcesInLocalstorage(updatedSources);
    toast({
      title: 'Sources Added',
      status: 'success',
      duration: 1000,
      isClosable: true
    });
  }

  useEffect(() => {
    if (!activeConversation) {
      setSources(sourcesFromLocalstorage || []);
    } else if (isCollectionInitiator && !!cards?.length) {
      const cardsAsSources = cards?.map((c) => ({ id: c.id, title: c.title, type: 'Card' }));
      setSources(cardsAsSources);
      setSourcesInLocalstorage(cardsAsSources);
    } else if (activeConversation?.sources?.length) {
      setSources(activeConversation?.sources);
      setSourcesInLocalstorage(activeConversation?.sources);
    }
  }, [activeConversation, cards]);

  return (
    <Stack
      gap={6}
      p={6}
      height='100%'
      overflow='auto'
      bgColor='neutral.onPrimary'
      rounded='xl'
    >
      {!!sources.length && (
        <Flex gap={2}>
          <Text
            variant='body-reg-md'
            color='surface.onSurfaceVariant'
          >
            {pluralized('sources', sources.length)} selected
          </Text>
          <Spacer />
          {!isCollectionInitiator && (
            <Button
              color='brandPrimary.primary'
              variant='link'
              size='md'
              px={8}
              onClick={onSearchModalOpen}
            >
              Add...
            </Button>
          )}
        </Flex>
      )}
      {!!sources?.length && isLoading && !cards?.length && (
        <Lottie
          style={{
            height: '90px',
            width: '90px',
            marginLeft: '-18px'
          }}
          animationData={animation}
        />
      )}
      {!!sources?.length &&
        cards &&
        cards?.length > 0 &&
        cards?.map((source) => (
          <ExplorerSourceCard
            key={source.id}
            card={source}
            onRemove={removeFn}
            hideMenu={isCollectionInitiator}
          />
        ))}

      {!sources?.length && (
        <Box
          layerStyle='section-card'
          borderRadius='md'
          p={12}
          textAlign='center'
        >
          <VStack gap={6}>
            <Center>
              <Icon
                as={SFClockIcon}
                boxSize={12}
                color='brandPrimary.primary'
              />
            </Center>

            <Heading
              size='lg'
              color='brandPrimary.primary'
            >
              No Selected Summaries
            </Heading>

            <Text color='surface.onSurfaceVariant'>Start by adding summaries</Text>

            <Button
              variant='reskinPrimary'
              size='md'
              px={8}
              onClick={onSearchModalOpen}
            >
              Add
            </Button>
          </VStack>
        </Box>
      )}
      <CardSearchModal
        disclosure={{
          isOpen: isSearchModalOpen,
          onOpen: onSearchModalOpen,
          onClose: onSearchModalClose
        }}
        submitFn={addSubmitFn}
      />
    </Stack>
  );
}

export default ExplorerSourcesContent;
